import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders } from '@angular/common/http';

import { Observable, BehaviorSubject, throwError, empty, of } from 'rxjs';
import { catchError, retry, timeout, tap, finalize, debounceTime, distinctUntilChanged } from 'rxjs/operators';
// import { CanLoad, Route, UrlSegment, Router } from '@angular/router';

import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class HttpService {
  public apiProtocol: string = environment.apiProtocol;
  public apiHostname: string = environment.apiHostname;
  public apiPort: number = environment.apiPort;
  public apiPrefix: string = environment.apiPrefix;
  public apiTimeout: number = environment.apiTimeout;
  public apiUrl: string;
  private requestSubject: BehaviorSubject<string> = new BehaviorSubject(null);
  private errorSubject: BehaviorSubject<any> = new BehaviorSubject(null);
  constructor(private http: HttpClient) {
    if (environment.production) {
      this.apiUrl = environment.apiUrl ? environment.apiUrl : `${this.apiProtocol}//${this.apiHostname}:${this.apiPort}/${this.apiPrefix}`;

    } else {
      if (!environment.apiUrl) {
        const url = new URL(window.location.href);
        if (url.port !== '') {
          this.apiUrl = `${url.protocol}//${url.hostname}:${environment.apiPort || url.port}/${environment.apiPrefix}`;
        } else {
          this.apiUrl = `${url.protocol}//${url.host}/${environment.apiPrefix}`;
        }
      } else {
        this.apiUrl = environment.apiUrl;
      }
    }
    // if (!this.activeRequest) {
    //   this.activeRequest = new BehaviorSubject('');
    // }
  }

  get $request() {
    return this.requestSubject.asObservable();
  }
  get $error() {
    return this.errorSubject.asObservable();
  }

  static setAuthLoadHeaders(token: string, userAgentId: string, stage?: string) {
    let headers = new HttpHeaders();
    headers = headers.set('Authorization', `Bearer ${token}`);
    headers = headers.set('UserAgentId', userAgentId);
    return headers;
  }
  static setAuthHeader(token: string, headers?: HttpHeaders) {
    if (!headers) { headers = new HttpHeaders(); }
    return headers.set('Authorization', `Bearer ${token}`);
  }
  static setFormMultipart(headers?: HttpHeaders) {
    if (!headers) { headers = new HttpHeaders(); }
    return headers.set('Content-Type', `multipart/form-data;`);
  }

  'post'(url: string, data: object, appHeader?): Observable<any> {
    this.requestSubject.next('Saving data');
    const headers = new HttpHeaders({
      'app-language': `${appHeader}`,
    });
    return this.http.post(this.apiUrl + url, data, { headers })
      .pipe(
        timeout(this.apiTimeout),
        catchError(this.handleError),
        finalize(() => this.endRequest())
      );
  }
  'postForm'(url: string, data: object, appHeader?, showLoader = false): Observable<any> {
    if (showLoader) {
      this.requestSubject.next('Saving data');
    }
    const headers = new HttpHeaders({
      'app-language': `${appHeader}`,
    });

    const formData = this.toFormData(data);
    return this.http.post(this.apiUrl + url, formData, {
      // reportProgress: true,
      // observe: 'events',
      headers
    }).pipe(
      catchError(this.handleError),
      finalize(() => this.endRequest())
    );
  }
  toFormData<T>( formValue: T ) {
    const formData = new FormData();
    for (const key of Object.keys(formValue)) {
      const value = formValue[key];
      if (Array.isArray(value)) {
        value.forEach(e => {
          for (const k of Object.keys(e)) {
            if (k !== 'preview') {
              if (k === 'file' && Array.isArray(e[k])) {
                formData.append(`${k}`, e[k][0].file );
              } else if (k === 'file' && e[k]?.files) {
                formData.append(`${k}`, e[k].files[0] );
              } else {
                formData.append(`${k}`, e[k]);
              }
            }
          }
        });
      } else {
        if (key === 'file' && value.files) {
          formData.append(`${key}`, value.files[0] );
        } else {
          formData.append(`${key}`, value);
        }
      }
    }
    return formData;
  }
  'get'(url: string, options?: any, appHeader?, requestTimeout?: number): Observable<any> {
    const headers = new HttpHeaders({
      'app-language': `${appHeader}`,
    });
    return this.http.get(this.apiUrl + url, { ...options, headers})
      .pipe(
        timeout(requestTimeout || this.apiTimeout),
        catchError((error) => this.handleError(error)),
        finalize(() => this.endRequest())
      );
  }
  'put'(url: string, data: object): Observable<any> {
    this.requestSubject.next('Updating data');
    return this.http.put(this.apiUrl + url, data)
      .pipe(
        timeout(this.apiTimeout),
        catchError(this.handleError),
        finalize(() => this.endRequest())
      );
  }
  'putForm'(url: string, data: object): Observable<any> {
    this.requestSubject.next('Updating data');
    const formData = this.obj2formData(data);
    return this.http.put(this.apiUrl + url, formData, {
      reportProgress: true,
      observe: 'events'
    }).pipe(
      catchError(this.handleError),
      finalize(() => this.endRequest())
    );
  }
  'delete'(url: string): Observable<any> {
    this.requestSubject.next('Deleting data');
    return this.http.delete(this.apiUrl + url)
      .pipe(
        timeout(this.apiTimeout),
        catchError(this.handleError),
        finalize(() => this.endRequest())
      );
  }


  private obj2formData(data: object) {
    const form = new FormData();
    Object.keys(data).forEach((name) => {
      if (typeof data[name] !== 'string') {
        if (data[name]) {
          if (data[name].type && data[name].type === 'upload') {
            data[name] = data[name].file;
          } else {
            data[name] = JSON.stringify(data[name]);
          }
        }
      }
      form.append(name, data[name]);
    });
    return form;
  }

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      let shouldThrow = true;
      if (!this.errorSubject) this.errorSubject = new BehaviorSubject(null);
      if (error.status === 0) {
        this.errorSubject.next(`Backend is down`);
      } else if (error.status === 400) {
        console.error('form error');
      } else if (error.status === 404) {
        console.log(`404 Error was here`, error);
        shouldThrow = false;
        return of(false);
      } else if (error.status === 422) {
        this.errorSubject.next(error);
        // console.error('validation error', error);
      } else if (error.status === 401) {
        if (error.error === "TokenExpiredError") {
          console.error('Token expired');
          shouldThrow = false;
          return empty();
        } else if (error.error === "Access denied") {
          console.error('Auth', error);
          sessionStorage.removeItem('token');
          localStorage.removeItem('token');
          throwError(error);
        } else {
          console.error('Auth', error);
          sessionStorage.removeItem('token');
          localStorage.removeItem('token');
          window.location.href = '/auth/login';
          throwError(error);
        }
      } else if (error.status === 500) {
        this.errorSubject.next(`Backend internal error`);
      } else {
        return throwError(error);
      //   console.log(error);
      //   // The backend returned an unsuccessful response code.
      //   // The response body may contain clues as to what went wrong,
      //   console.error(
      //     `Backend returned code ${error.status}, ` +
      //     `body was: ${error.error}`);
      }
      // return an observable with a user-facing error message
      if (shouldThrow) {
        return throwError(error);
      }
    }
  }
  private endRequest() {
    this.requestSubject.next(null);
  }
}
